import { inject, Injectable } from '@angular/core';
import { combineLatest, map, Observable } from 'rxjs';
import { MappingConfig, MappingStrategy, MappingType } from '../interfaces';
import { DockGroupMappingStrategy } from '../strategies/dock-group-mapping-strategy';
import { FreightTypeMappingStrategy } from '../strategies/freight-type-mapping-strategy';
import { LocationMappingStrategy } from '../strategies/location-mapping-strategy';

@Injectable({
  providedIn: 'root',
})
export class NameMappingPipeline {
  private locationStrategy = inject(LocationMappingStrategy);
  private freightTypeStrategy = inject(FreightTypeMappingStrategy);
  private dockGroupStrategy = inject(DockGroupMappingStrategy);

  public applyMappings<T>(data: T, configs: MappingConfig[]): Observable<T> {
    return this.waitForStrategiesReady(configs).pipe(map(() => this.applyMappingsToData(data, configs)));
  }

  public mapLocations<T>(data: T, mappings: { idField: string; nameField: string }[]): Observable<T> {
    const configs = mappings.map(({ idField, nameField }) => ({
      idField,
      nameField,
      strategy: MappingType.LOCATION,
    }));
    return this.applyMappings(data, configs);
  }

  public mapFreightTypes<T>(data: T, mappings: { idField: string; nameField: string }[]): Observable<T> {
    const configs = mappings.map(({ idField, nameField }) => ({
      idField,
      nameField,
      strategy: MappingType.FREIGHT_TYPE,
    }));
    return this.applyMappings(data, configs);
  }

  public mapDockGroups<T>(data: T, mappings: { idField: string; nameField: string }[]): Observable<T> {
    const configs = mappings.map(({ idField, nameField }) => ({
      idField,
      nameField,
      strategy: MappingType.DOCK_GROUP,
    }));
    return this.applyMappings(data, configs);
  }

  private applyMappingsToData<T>(data: T, configs: MappingConfig[]): T {
    return configs.reduce((currentData, config) => {
      const strategy = this.getStrategy(config);
      if (!strategy) {
        console.warn('No strategy found for config:', config);
        return currentData;
      }

      return this.applyMapping(currentData, config, strategy);
    }, data);
  }

  private getStrategy(config: MappingConfig): MappingStrategy | undefined {
    switch (config.strategy) {
      case MappingType.LOCATION:
        return this.locationStrategy;
      case MappingType.FREIGHT_TYPE:
        return this.freightTypeStrategy;
      case MappingType.DOCK_GROUP:
        return this.dockGroupStrategy;
      default:
        return undefined;
    }
  }

  private applyMapping<T>(data: T, config: MappingConfig, strategy: MappingStrategy): T {
    if (Array.isArray(data)) {
      return data.map((item) => {
        const id = item[config.idField as keyof typeof item];
        const name = strategy.resolve(id);

        return {
          ...item,
          [config.nameField]: name,
        };
      }) as T;
    } else {
      const id = data[config.idField as keyof T];
      const name = strategy.resolve(id);

      return {
        ...data,
        [config.nameField]: name,
      };
    }
  }

  private waitForStrategiesReady(configs: MappingConfig[]): Observable<boolean> {
    const strategyReadiness = configs
      .map((config) => this.getStrategy(config))
      .filter((strategy) => !!strategy)
      .map((strategy) => strategy!.isReady());

    if (strategyReadiness.length === 0) {
      return new Observable((subscriber) => {
        subscriber.next(true);
        subscriber.complete();
      });
    }

    return combineLatest(strategyReadiness).pipe(map((readiness) => readiness.every((ready) => ready)));
  }
}
